import { FormData, Stages } from '@/api/types';

export enum UserEnum {
  CONSULTANT = 'consultant',
  CLIENT = 'client',
}

export const getUserType = (data?: FormData | null): UserEnum =>
  data?.crm_user ? UserEnum.CONSULTANT : UserEnum.CLIENT;

export interface DataLayerItem {
  event: string;
  etap_id?: string;
  etap_nazwa?: string;
  formularz_id?: string;
  wersja_formularza?: string;
  powod_dyskwalifikacji?: string;
  popup_action?: string;
  popup_name?: string;
  user_type?: UserEnum;
}

declare global {
  interface Window {
    dataLayer: DataLayerItem[];
  }
}

export interface StageData {
  etap_id: string;
  etap_nazwa: string;
}

const isEventAlreadyInDataLayer = (
  eventName: string,
  idValue?: string
): boolean => {
  if (typeof window === 'undefined' || !window.dataLayer) return false;
  return window.dataLayer.some((item) => {
    const isEventMatch = item.event === eventName;
    if (!idValue) return isEventMatch;
    return isEventMatch && item.etap_id === idValue;
  });
};

interface FormDataLayerConfig {
  formularz_id: string;
  wersja_formularza: string;
  stageMap: Record<string, StageData>;
  // stage the disqualification / contact events are attributed to
  disqualificationStage: string;
}

/**
 * Builds a set of GTM dataLayer helpers bound to a single form configuration
 * (form id, version and the stage -> etap map). The household and the
 * vehicle-damage flows share the exact same event shape, they only differ in
 * the configuration below.
 */
const createFormDataLayer = (config: FormDataLayerConfig) => {
  const startForm = (user_type: UserEnum): void => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    if (!isEventAlreadyInDataLayer('formularz_rozpoczecie')) {
      window.dataLayer.push({
        event: 'formularz_rozpoczecie',
        user_type,
        formularz_id: config.formularz_id,
        wersja_formularza: config.wersja_formularza,
      });
    }
  };

  const startStage = (stage: string, user_type: UserEnum): void => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    const stageData = config.stageMap[stage];
    if (!stageData) return;
    if (!isEventAlreadyInDataLayer('formularz_etap_widok', stageData.etap_id)) {
      window.dataLayer.push({
        event: 'formularz_etap_widok',
        user_type,
        formularz_id: config.formularz_id,
        etap_id: stageData.etap_id,
        etap_nazwa: stageData.etap_nazwa,
      });
    }
  };

  const finishStage = (stage: string, user_type: UserEnum): void => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    const stageData = config.stageMap[stage];
    if (!stageData) return;
    if (
      !isEventAlreadyInDataLayer(
        'formularz_etap_zakonczenie',
        stageData.etap_id
      )
    ) {
      window.dataLayer.push({
        event: 'formularz_etap_zakonczenie',
        user_type,
        formularz_id: config.formularz_id,
        etap_id: stageData.etap_id,
        etap_nazwa: stageData.etap_nazwa,
      });
    }
  };

  const handleNextStage = (
    stage: string,
    nextStage: string,
    user_type: UserEnum
  ): void => {
    finishStage(stage, user_type);
    startStage(nextStage, user_type);
  };

  const notQualified = (
    reason: string,
    type: 'contact_us' | 'not_qaulify' = 'not_qaulify',
    user_type: UserEnum
  ): void => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    const stageData = config.stageMap[config.disqualificationStage];
    if (!stageData) return;
    const eventName =
      type === 'contact_us'
        ? 'formularz_ponowny_kontakt'
        : 'formularz_dyskwalifikacja';
    if (!isEventAlreadyInDataLayer(eventName, stageData.etap_id)) {
      window.dataLayer.push({
        event: eventName,
        user_type,
        formularz_id: config.formularz_id,
        etap_id: stageData.etap_id,
        etap_nazwa: stageData.etap_nazwa,
        powod_dyskwalifikacji: reason,
      });
    }
  };

  return {
    startForm,
    startStage,
    finishStage,
    handleNextStage,
    notQualified,
  };
};

/* -------------------------------------------------------------------------- */
/*                              Household (HH)                                 */
/* -------------------------------------------------------------------------- */

export enum StageId {
  VERIFICATION = 'stage_I',
  DAMAGE_ADDRESS = 'stage_II',
  DOCUMENTS = 'stage_III',
  FINISHED = 'finished',
}

export enum StageName {
  VERIFICATION = 'Weryfikacja szkody',
  DAMAGE_ADDRESS = 'Adres szkody',
  DOCUMENTS = 'Dokumenty',
  FINISHED = 'Zakończ',
}

const stageToDataLayerMap: Record<string, StageData> = {
  stage_I: {
    etap_id: StageId.VERIFICATION,
    etap_nazwa: StageName.VERIFICATION,
  },
  stage_II: {
    etap_id: StageId.DAMAGE_ADDRESS,
    etap_nazwa: StageName.DAMAGE_ADDRESS,
  },
  stage_III: { etap_id: StageId.DOCUMENTS, etap_nazwa: StageName.DOCUMENTS },
  finished: { etap_id: StageId.FINISHED, etap_nazwa: StageName.FINISHED },
};

const householdDataLayer = createFormDataLayer({
  formularz_id: 'household',
  wersja_formularza: '1.0',
  stageMap: stageToDataLayerMap,
  disqualificationStage: Stages.STAGE_I,
});

export const startFormDataLayer = householdDataLayer.startForm;
export const startStageDataLayer = householdDataLayer.startStage;
export const finishStageDataLayer = householdDataLayer.finishStage;
export const handleNextStageDataLayer = householdDataLayer.handleNextStage;
export const notQualifiedDataLayer = householdDataLayer.notQualified;

/* -------------------------------------------------------------------------- */
/*                            Vehicle damage (VD)                             */
/* -------------------------------------------------------------------------- */

export enum VehicleDamageStageId {
  VERIFICATION = 'stage_I',
  DOCUMENTS = 'stage_I_documents',
  ADDITIONAL_INFORMATION = 'stage_II',
  SURVEY_FINISHED = 'stage_II_finished',
  OFFER = 'stage_III',
  COMPLEMENTARY = 'stage_IV_complementary',
  TRANSFER_DETAILS = 'stage_IV',
  SIGN_DOCUMENTS = 'stage_V',
  DOCUMENTS_VERIFICATION = 'stage_V_finished',
}

export enum VehicleDamageStageName {
  VERIFICATION = 'Weryfikacja szkody',
  DOCUMENTS = 'Dokumenty',
  ADDITIONAL_INFORMATION = 'Dodatkowe informacje',
  SURVEY_FINISHED = 'Zakończenie ankiety',
  OFFER = 'Oferta',
  COMPLEMENTARY = 'Uzupełnienie informacji',
  TRANSFER_DETAILS = 'Dane do przelewu',
  SIGN_DOCUMENTS = 'Podpisanie dokumentów',
  DOCUMENTS_VERIFICATION = 'Weryfikacja dokumentów',
}

// Keyed by etap_id (the canonical funnel step), not by raw backend stage –
// several backend stages collapse onto a single etap (see the resolver below).
const vehicleDamageStageToDataLayerMap: Record<string, StageData> = {
  [VehicleDamageStageId.VERIFICATION]: {
    etap_id: VehicleDamageStageId.VERIFICATION,
    etap_nazwa: VehicleDamageStageName.VERIFICATION,
  },
  [VehicleDamageStageId.DOCUMENTS]: {
    etap_id: VehicleDamageStageId.DOCUMENTS,
    etap_nazwa: VehicleDamageStageName.DOCUMENTS,
  },
  [VehicleDamageStageId.ADDITIONAL_INFORMATION]: {
    etap_id: VehicleDamageStageId.ADDITIONAL_INFORMATION,
    etap_nazwa: VehicleDamageStageName.ADDITIONAL_INFORMATION,
  },
  [VehicleDamageStageId.SURVEY_FINISHED]: {
    etap_id: VehicleDamageStageId.SURVEY_FINISHED,
    etap_nazwa: VehicleDamageStageName.SURVEY_FINISHED,
  },
  [VehicleDamageStageId.OFFER]: {
    etap_id: VehicleDamageStageId.OFFER,
    etap_nazwa: VehicleDamageStageName.OFFER,
  },
  [VehicleDamageStageId.COMPLEMENTARY]: {
    etap_id: VehicleDamageStageId.COMPLEMENTARY,
    etap_nazwa: VehicleDamageStageName.COMPLEMENTARY,
  },
  [VehicleDamageStageId.TRANSFER_DETAILS]: {
    etap_id: VehicleDamageStageId.TRANSFER_DETAILS,
    etap_nazwa: VehicleDamageStageName.TRANSFER_DETAILS,
  },
  [VehicleDamageStageId.SIGN_DOCUMENTS]: {
    etap_id: VehicleDamageStageId.SIGN_DOCUMENTS,
    etap_nazwa: VehicleDamageStageName.SIGN_DOCUMENTS,
  },
  [VehicleDamageStageId.DOCUMENTS_VERIFICATION]: {
    etap_id: VehicleDamageStageId.DOCUMENTS_VERIFICATION,
    etap_nazwa: VehicleDamageStageName.DOCUMENTS_VERIFICATION,
  },
};

export const vehicleDamageDataLayer = createFormDataLayer({
  formularz_id: 'vehicle_damage',
  wersja_formularza: '1.0',
  stageMap: vehicleDamageStageToDataLayerMap,
  disqualificationStage: VehicleDamageStageId.VERIFICATION,
});

/**
 * Maps a raw backend stage onto its funnel etap_id. Returns undefined for
 * transient ("_finished"/"_conflict") and out-of-funnel stages so that no event
 * is emitted for them – the finish event of the preceding etap fires naturally
 * when the user leaves it.
 *
 * stage_IV is special: it renders the complementary form or the transfer
 * details depending on `isComplementary`, so it resolves to two funnel steps.
 */
const resolveVehicleDamageEtapId = (
  stage: string,
  isComplementary: boolean
): VehicleDamageStageId | undefined => {
  switch (stage) {
    case Stages.STAGE_I:
      return VehicleDamageStageId.VERIFICATION;
    case Stages.STAGE_I_DOCUMENTS:
      return VehicleDamageStageId.DOCUMENTS;
    case Stages.STAGE_II:
      return VehicleDamageStageId.ADDITIONAL_INFORMATION;
    case Stages.STAGE_II_FINISHED:
      return VehicleDamageStageId.SURVEY_FINISHED;
    case Stages.STAGE_III:
      return VehicleDamageStageId.OFFER;
    case Stages.STAGE_IV_COMPLEMENTARY:
      return VehicleDamageStageId.COMPLEMENTARY;
    case Stages.STAGE_IV:
      return isComplementary
        ? VehicleDamageStageId.COMPLEMENTARY
        : VehicleDamageStageId.TRANSFER_DETAILS;
    case Stages.STAGE_V:
      return VehicleDamageStageId.SIGN_DOCUMENTS;
    case Stages.STAGE_V_FINISHED:
      return VehicleDamageStageId.DOCUMENTS_VERIFICATION;
    default:
      return undefined;
  }
};

// Funnel steps whose completion should emit a "formularz_etap_zakonczenie"
// event – i.e. every tracked etap except the terminal one ("Weryfikacja
// dokumentów"), which, like HH's terminal etap, only ever receives a view event.
const VEHICLE_DAMAGE_FINISH_EVENT_IDS: string[] = [
  VehicleDamageStageId.VERIFICATION,
  VehicleDamageStageId.DOCUMENTS,
  VehicleDamageStageId.ADDITIONAL_INFORMATION,
  VehicleDamageStageId.SURVEY_FINISHED,
  VehicleDamageStageId.OFFER,
  VehicleDamageStageId.COMPLEMENTARY,
  VehicleDamageStageId.TRANSFER_DETAILS,
  VehicleDamageStageId.SIGN_DOCUMENTS,
];

// Disqualification branches – landing on them must not be reported as a normal
// stage completion (the dedicated dyskwalifikacja / ponowny_kontakt events are
// fired from the NotQualify / ContactUs screens instead).
const VEHICLE_DAMAGE_DISQUALIFICATION_STAGES: string[] = [
  Stages.NOT_QUALIFY,
  Stages.CONTACT_US,
];

/**
 * Reports a vehicle-damage stage change to the dataLayer:
 *  - fires the view event for the etap the user just landed on,
 *  - fires the finish event for the etap they just left,
 *    unless they dropped into a disqualification branch.
 * No-op for stages that are not part of the tracked funnel.
 */
export const handleVehicleDamageStageView = (
  currentStage: string,
  previousStage: string | undefined,
  isComplementary: boolean,
  user_type: UserEnum
): void => {
  const currentEtapId = resolveVehicleDamageEtapId(
    currentStage,
    isComplementary
  );
  if (currentEtapId) {
    vehicleDamageDataLayer.startStage(currentEtapId, user_type);
  }

  if (
    !previousStage ||
    previousStage === currentStage ||
    VEHICLE_DAMAGE_DISQUALIFICATION_STAGES.includes(currentStage)
  ) {
    return;
  }

  const previousEtapId = resolveVehicleDamageEtapId(
    previousStage,
    isComplementary
  );
  if (
    previousEtapId &&
    VEHICLE_DAMAGE_FINISH_EVENT_IDS.includes(previousEtapId)
  ) {
    vehicleDamageDataLayer.finishStage(previousEtapId, user_type);
  }
};
